import { useCallback, useRef, useState } from 'react';
import type { Task } from '@/components/ui/columns-tasks';
import type { useDeadlineActions } from '@/hooks/useDeadlineActions';
import type { BulkDeadlineChanges } from '@/lib/deadlines/bulk-edit';

type DeadlineActions = ReturnType<typeof useDeadlineActions>;

interface UseDeadlineMutationsUiArgs {
  taskActions: DeadlineActions;
  allTasks: Task[];
}

/** Delete / bulk-delete / bulk-edit / restore dialog state and their async
 *  handlers for the deadlines table. Errors are surfaced by useDeadlineActions via
 *  toast, so the handlers here only manage the surrounding UI (busy flags,
 *  dialogs, selection). */
export function useDeadlineMutationsUi({ taskActions, allTasks }: UseDeadlineMutationsUiArgs) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [selectedDeadlinesForBulkEdit, setSelectedDeadlinesForBulkEdit] = useState<Task[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const clearBulkEditSelectionRef = useRef<() => void>(() => {});

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      const task = allTasks.find((t) => t.id === taskId);
      if (task) {
        setTaskToDelete(task);
        setDeleteDialogOpen(true);
      }
    },
    [allTasks],
  );

  const confirmDelete = useCallback(async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    try {
      await taskActions.deleteDeadline(taskToDelete, true); // Skip confirmation since we have our own dialog
    } catch {
      // Intentional no-op: useDeadlineActions already surfaces a toast.error.
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  }, [taskToDelete, taskActions]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
    setIsDeleting(false);
  }, []);

  // Bulk delete handler. Resolve selected ids back to rows so the action can
  // partition synthetic (soft-delete) vs legacy (hard-delete) deadlines.
  const handleBulkDelete = useCallback(
    async (selectedIds: string[]) => {
      setIsBulkDeleting(true);
      try {
        const rows = selectedIds
          .map((id) => allTasks.find((t) => t.id === id))
          .filter((t): t is Task => Boolean(t));
        await taskActions.bulkDeleteDeadlines(rows);
      } catch {
        // Intentional no-op: useDeadlineActions already surfaces a toast.error.
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [allTasks, taskActions],
  );

  // Restore handlers (Table "Show deleted" view only).
  const handleRestoreDeadline = useCallback(
    (syntheticId: string) => taskActions.restoreDeadline(syntheticId),
    [taskActions],
  );

  const handleBulkRestore = useCallback(
    async (selectedIds: string[]) => {
      const syntheticIds = selectedIds
        .map((id) => allTasks.find((t) => t.id === id))
        .filter((t): t is Task => Boolean(t))
        .map((t) => t.synthetic_deadline_id)
        .filter((id): id is string => !!id);
      await taskActions.bulkRestoreDeadlines(syntheticIds);
    },
    [allTasks, taskActions],
  );

  const handleBulkUpdate = useCallback(
    async (changes: BulkDeadlineChanges) => {
      setIsBulkUpdating(true);
      try {
        await taskActions.bulkUpdateDeadlines(changes);
        setBulkEditDialogOpen(false);
        setSelectedDeadlinesForBulkEdit([]);
        clearBulkEditSelectionRef.current();
      } catch {
        // Intentional no-op: useDeadlineActions already surfaces a toast.error.
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [taskActions],
  );

  return {
    deleteDialogOpen,
    setDeleteDialogOpen,
    taskToDelete,
    isDeleting,
    isBulkDeleting,
    bulkEditDialogOpen,
    setBulkEditDialogOpen,
    selectedDeadlinesForBulkEdit,
    setSelectedDeadlinesForBulkEdit,
    isBulkUpdating,
    clearBulkEditSelectionRef,
    handleDeleteTask,
    confirmDelete,
    handleCancelDelete,
    handleBulkDelete,
    handleRestoreDeadline,
    handleBulkRestore,
    handleBulkUpdate,
  };
}
